/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  RequestUtilizationEfficiencyReportsInterFace,
  UtilizationEfficiencyReportsInitialProperties,
  UtilizationEfficiencyReportsResponseInterface,
} from '../../interface/utilization-efficiency-report-interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import stationManagement from '../../utils/api/station-management';
// a thunk to fetch users
export const fetchUtilizationEfficiencyReports = createAsyncThunk<
  UtilizationEfficiencyReportsResponseInterface,
  RequestUtilizationEfficiencyReportsInterFace
>(
  'reports-utilization-efficiency/fetchUtilizationEfficiencyReports',
  async (data: RequestUtilizationEfficiencyReportsInterFace, { dispatch }) => {
    try {
      const response = await stationManagement.fetchReportUtilization(removeEmptyKeys({ ...data }));
      return response.data;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// a thunk to fetch devices filters
export const fetchListUtilizationEfficiencyReportsFilters = createAsyncThunk<
  CommonFilterProperties[],
  { chargingStation: string }
>(
  'reports-utilization-efficiency/fetchListUtilizationEfficiencyReportsFilters',
  async ({ chargingStation }, { dispatch }) => {
    if (!chargingStation) {
      throw new Error('chargingStation is required');
    }
    try {
      const response = await stationManagement.fetchReportUtilizationFilters({
        chargingStation,
      });
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

// Apply default filters
const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(today.getDate() - 1);

/**
 * Filter date format func
 * @param {any} date The date to format.
 * @returns {any} The formatted date string.
 */
const formatDate = (date: Date): string => date.toISOString().split('T')[0];

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
  {
    name: 'createdDateRange',
    key: 'startDate_endDate',
    value: '',
    key1: 'startDate',
    value1: formatDate(yesterday),
    key2: 'endDate',
    value2: formatDate(today),
    type: 'dateRange',
    listOfValue: [],
    applyFilter: true,
    enable: true,
    disableClearFilter: true,
    disableClearFilterMessage: 'Date filter is required!',
    FEAddedFilter: true, // This is used to identify if the filter is added from FE
  },
];

// Initial state
const initialState: UtilizationEfficiencyReportsInitialProperties = {
  UtilizationEfficiencyReports: [],
  limit: DefaultApiLimit.limit,
  page: DefaultApiLimit.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

// Create the slice
export const UtilizationEfficiencyReportsSlice = createSlice({
  name: 'reports-utilization-efficiency',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param {any} state The current slice state.
     * @param {any} action The dispatched action containing filter update properties.
     */
    updateUtilizationEfficiencyReportsFilters: (
      state,
      action: PayloadAction<FilterUpdateProperties>,
    ) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param {any} state The current slice state.
     */
    disableUtilizationEfficiencyReportsFiltersApplied: (state) => {
      state.isFilterUpdated = false;
    },
    /**
     * function for set sortBy
     * @param {any} state The current slice state.
     * @param {any} action The dispatched action containing sort field and order.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      console.log('Redux:', action.payload.sortField);
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Use the extra reducers function
   * @param {any} builder The builder for handling extra reducers.
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchUtilizationEfficiencyReports.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUtilizationEfficiencyReports.fulfilled, (state, action) => {
        state.loading = false;
        state.UtilizationEfficiencyReports = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchUtilizationEfficiencyReports.rejected, (state) => {
        state.loading = false;
      });
    builder
      .addCase(fetchListUtilizationEfficiencyReportsFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListUtilizationEfficiencyReportsFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...availableFilters, ...action.payload];
        },
      )
      .addCase(fetchListUtilizationEfficiencyReportsFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});
// Export the actions
export const {
  updateUtilizationEfficiencyReportsFilters,
  disableUtilizationEfficiencyReportsFiltersApplied,
  setSortData,
} = UtilizationEfficiencyReportsSlice.actions;

// Export the reducer
export default UtilizationEfficiencyReportsSlice.reducer;
